import React, { useEffect, useState, useCallback } from "react";
import {
  fetchLeague, applyToLeague, fetchLeagueApplications, decideApplication,
  fetchTeams, createTeam, fetchTournaments, createTournament,
  fetchPlayers, fetchBadges, createInvite,
  fetchAvailablePlayers, removeTeamMember, createTeamInvite,
  inviteToTeamMailbox, fetchUpcomingGames, createChallenge,
  inviteToLeagueMailbox, requestToJoinTeam,
  fetchHqs, inviteHqToLeague,
  recordChallengeResult,
  fetchLeagueDmThreads, fetchLeagueDmThread, sendLeagueDm,
  transferLeagueOwnership, setLeagueManager, upgradeLeagueToPro,
  deleteLeague, resetSandbox,
  cancelMembership,
  fetchLeagueLadder,
  listLeagueStreams,
} from "../lib/api";
import { AvatarChip } from "./AvatarChip";
import { TierBadge } from "./TierBadge";
import { useAuth } from "../context/AuthContext";
import { BracketView } from "./BracketView";
import { BadgeShareModal } from "./BadgeShareModal";
import { LeagueChat } from "./LeagueChat";
import { LeagueHeatmap } from "./LeagueHeatmap";
import { LeagueEditModal } from "./LeagueEditModal";
import { LeagueModerationPanel } from "./LeagueModerationPanel";
import { LeagueClipsPanel } from "./LeagueClipsPanel";
import { AttachTeamPicker } from "./AttachTeamPicker";
import { TeamEditModal } from "./TeamEditModal";
import { ChallengeModal } from "./ChallengeModal";
import { BoostBar } from "./BoostBar";

const SLOT_LABELS = { morning: "AM", afternoon: "PM", evening: "EVE", night: "LATE" };
const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const STATUS_STYLES = {
  pending: "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40",
  accepted: "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40",
  rejected: "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40",
};

const TOURNAMENT_STYLES = [
  { value: "round-robin", label: "Round Robin" },
  { value: "single-elim", label: "Single Elimination" },
  { value: "double-elim", label: "Double Elimination" },
  { value: "swiss",       label: "Swiss" },
  { value: "best-of",     label: "Best of Series" },
];

function TeamForm({ leagueId, members, onCreated, onCancel, onToast }) {
  const [name, setName] = useState("");
  const [avatarId, setAvatarId] = useState(0);
  const [social, setSocial] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const togglePick = (uid) => {
    setPicked((p) => p.includes(uid) ? p.filter(x => x !== uid) : [...p, uid]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { onToast("Team name required."); return; }
    setBusy(true);
    try {
      const t = await createTeam(leagueId, {
        name, avatar_id: avatarId, member_user_ids: picked, social_link: social,
      });
      onToast(`🎮 Team "${t.name}" created (MMR ${t.mmr})`);
      onCreated();
    } catch (e) {
      onToast(e?.response?.data?.detail || "Team creation failed.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3 glass rounded-2xl p-4" data-testid="team-form">
      <input
        placeholder="Team name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="team-name-input"
        className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-2.5 text-white"
      />
      <div>
        <p className="text-[10px] tracking-widest text-[#14b8a6] mb-2">TEAM AVATAR</p>
        <div className="grid grid-cols-6 gap-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <AvatarChip
              key={i}
              avatarId={i}
              size={44}
              selected={avatarId === i}
              onClick={() => setAvatarId(i)}
              dataTestid={`team-avatar-${i}`}
            />
          ))}
        </div>
      </div>
      <input
        placeholder="Social link (Discord / X / Twitch)"
        value={social}
        onChange={(e) => setSocial(e.target.value)}
        data-testid="team-social-input"
        className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-2.5 text-white"
      />
      <div>
        <p className="text-[10px] tracking-widest text-[#14b8a6] mb-2">
          PICK TEAMMATES (you're captain by default)
        </p>
        <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto">
          {members.filter(m => m.user_id !== undefined).map((m) => (
            <button
              type="button"
              key={m.user_id}
              onClick={() => togglePick(m.user_id)}
              data-testid={`team-pick-${m.user_id}`}
              className={`text-left px-3 py-2 rounded-xl border text-xs flex items-center gap-2 ${
                picked.includes(m.user_id)
                  ? "border-[#14b8a6] bg-[#14b8a6]/15 text-white"
                  : "border-white/15 text-white/70 hover:border-white/40"
              }`}
            >
              <AvatarChip avatarId={m.avatar_id} pfpUrl={m.pfp_url} size={28} />
              <span className="truncate">{m.name}</span>
              <span className="ml-auto text-[10px] text-[#a3a3ac]">{m.elo}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="btn-ghost px-4 py-2 rounded-xl text-sm">
          Cancel
        </button>
        <button type="submit" disabled={busy} data-testid="team-submit" className="btn-neon px-5 py-2 rounded-xl text-sm">
          {busy ? "CREATING..." : "CREATE TEAM"}
        </button>
      </div>
    </form>
  );
}

function TournamentForm({ leagueId, teams, onCreated, onCancel, onToast }) {
  const [name, setName] = useState("");
  const [style, setStyle] = useState("round-robin");
  const [date, setDate] = useState("");
  const [season, setSeason] = useState("");
  const [desc, setDesc] = useState("");
  const [picked, setPicked] = useState([]);
  const [swissRounds, setSwissRounds] = useState("");
  const [busy, setBusy] = useState(false);

  const togglePick = (tid) =>
    setPicked((p) => p.includes(tid) ? p.filter(x => x !== tid) : [...p, tid]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !date) { onToast("Name + date required."); return; }
    setBusy(true);
    try {
      const body = {
        name, style, start_date: date, season, team_ids: picked, description: desc,
      };
      if (style === "swiss" && swissRounds) {
        body.swiss_rounds = Math.max(1, Math.min(20, parseInt(swissRounds) || 0));
      }
      const t = await createTournament(leagueId, body);
      onToast(`🏆 Tournament "${t.name}" scheduled.`);
      onCreated();
    } catch (e) {
      onToast(e?.response?.data?.detail || "Tournament creation failed.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3 glass rounded-2xl p-4" data-testid="tournament-form">
      <input
        placeholder="Tournament name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="tour-name-input"
        className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-2.5 text-white"
      />
      <input
        placeholder="🗓️ Season (e.g. Winter 2026, S1)"
        value={season}
        onChange={(e) => setSeason(e.target.value)}
        data-testid="tour-season-input"
        className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-2.5 text-white"
      />
      <div className="grid grid-cols-2 gap-3">
        <select
          value={style}
          onChange={(e) => setStyle(e.target.value)}
          data-testid="tour-style-select"
          className="bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-2.5 text-white"
        >
          {TOURNAMENT_STYLES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="tour-date-input"
          className="bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-2.5 text-white"
        />
      </div>
      {style === "swiss" && (
        <input
          type="number"
          min="1" max="20"
          placeholder="Number of Swiss rounds (auto if blank)"
          value={swissRounds}
          onChange={(e) => setSwissRounds(e.target.value)}
          data-testid="tour-swiss-rounds-input"
          className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-2.5 text-white"
        />
      )}
      <textarea
        rows="2"
        placeholder="Rules / prize / format..."
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        data-testid="tour-desc-input"
        className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-2.5 text-white"
      />
      <div>
        <p className="text-[10px] tracking-widest text-[#14b8a6] mb-2">
          ENROLL TEAMS ({picked.length} selected)
        </p>
        {teams.length === 0 ? (
          <p className="text-xs text-white/50">No teams in this league yet — create one first.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto">
            {teams.map((t) => (
              <button
                type="button"
                key={t.team_id}
                onClick={() => togglePick(t.team_id)}
                data-testid={`tour-pick-${t.team_id}`}
                className={`text-left px-3 py-2 rounded-xl border text-xs flex items-center gap-2 ${
                  picked.includes(t.team_id)
                    ? "border-[#14b8a6] bg-[#14b8a6]/15"
                    : "border-white/15 text-white/70 hover:border-white/40"
                }`}
              >
                <AvatarChip avatarId={t.avatar_id} size={28} />
                <span className="truncate">{t.name}</span>
                <span className="ml-auto text-[10px] text-[#a3a3ac]">MMR {t.mmr}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="btn-ghost px-4 py-2 rounded-xl text-sm">
          Cancel
        </button>
        <button type="submit" disabled={busy} data-testid="tour-submit" className="btn-neon px-5 py-2 rounded-xl text-sm">
          {busy ? "SCHEDULING..." : "🏆 SCHEDULE"}
        </button>
      </div>
    </form>
  );
}

function InvitePlayerWidget({ leagueId, currentMemberIds, currentUserId, ownerUserId, onInvited, onToast }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setBusy(true);
    try {
      const all = await fetchPlayers({});
      const ql = q.toLowerCase();
      // Exclude existing members, the league owner, the current viewer
      // (you can't invite yourself), and obvious synthetic test users.
      const excluded = new Set([
        ...(currentMemberIds || []),
        currentUserId,
        ownerUserId,
      ].filter(Boolean));
      const isTestUser = (p) =>
        (p.user_id || "").startsWith("user_test_") ||
        /^(Neon Tester|Fresh Operator|QA_|TEST_|TST_)/i.test(p.name || "");
      const filtered = all
        .filter((p) => !excluded.has(p.user_id))
        .filter((p) => !isTestUser(p))
        .filter((p) => !q || p.name.toLowerCase().includes(ql))
        .slice(0, 8);
      setResults(filtered);
    } finally { setBusy(false); }
  };

  useEffect(() => { search(); }, [currentMemberIds, currentUserId, ownerUserId]); // eslint-disable-line

  const add = async (uid, displayName) => {
    try {
      const r = await inviteToLeagueMailbox(leagueId, uid);
      onToast(r.already_pending
        ? `📬 ${displayName || "Player"} already has a pending invite.`
        : `📬 Invite sent to ${displayName || "the player"}'s inbox.`);
      onInvited();
    } catch (e) {
      onToast(e?.response?.data?.detail || "Invite failed.");
    }
  };

  return (
    <div className="glass rounded-2xl p-4" data-testid="invite-widget">
      <p className="text-[10px] tracking-widest text-[#14b8a6] mb-2">📨 INVITE A PLAYER TO YOUR LEAGUE</p>
      <p className="text-[10px] text-white/50 mb-3">Invites land in the operator's INBOX — they accept or decline.</p>
      <div className="flex gap-2 mb-3">
        <input
          placeholder="Search by name..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
          data-testid="invite-search-input"
          className="flex-1 bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-sm text-white"
        />
        <button
          type="button"
          onClick={search}
          disabled={busy}
          data-testid="invite-search-btn"
          className="btn-ghost px-4 py-2 rounded-xl text-xs"
        >
          <i className="fas fa-search" />
        </button>
      </div>
      {results.length === 0 ? (
        <p className="text-xs text-white/50">No matches. Try another name.</p>
      ) : (
        <div className="space-y-2 max-h-44 overflow-y-auto">
          {results.map((p) => (
            <div key={p.user_id} className="flex items-center gap-2 bg-black/30 rounded-xl px-3 py-2" data-testid={`invite-row-${p.user_id}`}>
              <AvatarChip avatarId={p.avatar_id} pfpUrl={p.pfp_url} size={32} />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{p.name}</p>
                <p className="text-[10px] text-[#a3a3ac]">MMR {p.elo} • {(p.user_type || "casual").toUpperCase()}</p>
              </div>
              <button
                type="button"
                onClick={() => add(p.user_id, p.name)}
                data-testid={`invite-add-${p.user_id}`}
                className="btn-neon rounded-xl text-xs px-3 py-1.5"
              >
                INVITE
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LeagueDmLauncher({ league, onPick, onToast }) {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [allLeagues, setAllLeagues] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    fetchLeagueDmThreads(league.league_id)
      .then(setThreads)
      .catch(() => setThreads([]));
    // Reuse the existing /leagues listing to find DM targets.
    import("../lib/api").then(({ fetchLeagues }) => {
      fetchLeagues().then((rows) =>
        setAllLeagues((rows || []).filter((l) => l.league_id !== league.league_id))
      ).catch(() => setAllLeagues([]));
    });
  }, [open, league.league_id]);

  const filtered = (q
    ? allLeagues.filter((l) => (l.name || "").toLowerCase().includes(q.toLowerCase()))
    : allLeagues
  ).slice(0, 20);

  return (
    <div className="mt-3 pt-3 border-t border-[#14b8a6]/30" data-testid="league-dm-launcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="league-dm-launcher-toggle"
        className="rounded-2xl bg-black/40 border border-[#14b8a6]/50 text-[#14b8a6] font-bold px-4 py-2 text-xs tracking-widest hover:bg-[#14b8a6]/15"
      >
        {open ? "× CLOSE LEAGUE DMS" : "💬 DM ANOTHER LEAGUE"}
      </button>
      {open && (
        <div className="mt-3 glass rounded-2xl p-3 border border-[#14b8a6]/30 space-y-3">
          {threads.length > 0 && (
            <div>
              <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold mb-1.5">
                RECENT THREADS
              </p>
              <div className="space-y-1.5">
                {threads.slice(0, 5).map((t) => (
                  <button
                    type="button"
                    key={t.other_league_id}
                    onClick={() => onPick({
                      league_id: t.other_league_id,
                      name: t.other_league_name,
                      slug: t.other_league_slug,
                    })}
                    data-testid={`league-dm-thread-${t.other_league_id}`}
                    className="w-full text-left p-2 rounded-xl bg-black/30 border border-white/10 hover:border-[#14b8a6]/40"
                  >
                    <p className="text-[12px] font-bold">{t.other_league_name}</p>
                    <p className="text-[10px] text-white/55 truncate">{t.last_body}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold mb-1.5">
              FIND ANOTHER LEAGUE
            </p>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search league name…"
              data-testid="league-dm-search"
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm mb-2"
            />
            <div className="max-h-40 overflow-y-auto space-y-1">
              {filtered.length === 0 ? (
                <p className="text-[11px] text-white/40 text-center py-2">No matches.</p>
              ) : filtered.map((l) => (
                <button
                  type="button"
                  key={l.league_id}
                  onClick={() => onPick({ league_id: l.league_id, name: l.name, slug: l.slug })}
                  data-testid={`league-dm-pick-${l.league_id}`}
                  className="w-full text-left p-2 rounded-xl bg-black/30 border border-white/10 hover:border-[#14b8a6]/40"
                >
                  <p className="text-[12px] font-bold">{l.name}</p>
                  <p className="text-[10px] text-[#a3a3ac]">{l.game} · {l.region || "—"} · {l.league_type}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function GovernancePanel({ league, onChanged, onClose, onToast }) {
  const [busy, setBusy] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const isSandbox = !!league.is_sandbox;
  const members = (league.members_detail || [])
    .filter((m) => m.user_id !== league.owner_user_id)
    .filter((m) => !m.is_bot);  // never offer bots as managers/owners
  const managerId = league.manager_user_id;

  const transfer = async (uid) => {
    const u = members.find((m) => m.user_id === uid);
    if (!u) return;
    if (!window.confirm(
      `Transfer ownership of '${league.name}' to ${u.name}? You'll stay in the league as a regular member.`
    )) return;
    setBusy(`xfer-${uid}`);
    try {
      await transferLeagueOwnership(league.league_id, uid);
      onToast?.(`👑 Ownership handed to ${u.name}.`);
      await onChanged?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't transfer ownership.");
    } finally { setBusy(""); }
  };

  const assignMgr = async (uid) => {
    setBusy(`mgr-${uid || "clear"}`);
    try {
      await setLeagueManager(league.league_id, uid);
      onToast?.(uid ? "🧭 Manager assigned." : "Manager cleared.");
      await onChanged?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't set manager.");
    } finally { setBusy(""); }
  };

  const handleDelete = async () => {
    if (confirmText.trim() !== league.name) {
      onToast?.(`Type the league name exactly to confirm: "${league.name}"`);
      return;
    }
    if (!window.confirm(
      `Last warning — delete '${league.name}' and all its tournaments, applications, and team challenges?\n\nThis cannot be undone.`
    )) return;
    setBusy("delete");
    try {
      await deleteLeague(league.league_id);
      onToast?.(`🗑 League '${league.name}' deleted.`);
      onClose?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't delete league.");
    } finally { setBusy(""); }
  };

  const handleReset = async () => {
    if (!window.confirm(
      "Wipe all sandbox activity — tournaments, matches, team challenges, scheduled matches? Bots and the league itself will stay."
    )) return;
    setBusy("reset");
    try {
      const r = await resetSandbox(league.league_id);
      onToast?.(`🔄 Sandbox reset. Cleared ${r.tournaments || 0} tournaments, ${r.matches || 0} matches.`);
      await onChanged?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't reset sandbox.");
    } finally { setBusy(""); }
  };

  return (
    <div
      className="mt-3 pt-3 border-t border-[#14b8a6]/30 space-y-3"
      data-testid="league-governance-panel"
    >
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
        <i className="fas fa-scale-balanced mr-1" />GOVERNANCE
      </p>

      {isSandbox && (
        <div
          data-testid="sandbox-banner"
          className="rounded-xl p-3 border border-[#14b8a6]/40 bg-[#14b8a6]/5 text-[11px] text-white/80"
        >
          <p className="font-bold text-[#14b8a6] tracking-widest mb-1">
            <i className="fas fa-flask mr-1" />SANDBOX MODE
          </p>
          This is your private playground — invisible to other players,
          excluded from rankings, leaderboards and search. Use it to dry-run
          features against bot opponents. It can't be transferred or deleted.
        </div>
      )}

      {!isSandbox && (
        <div>
          <p className="text-[11px] text-white/70 mb-1.5">
            <i className="fas fa-crown mr-1 text-[#14b8a6]" />Transfer ownership
          </p>
          {members.length === 0 ? (
            <p className="text-[11px] text-white/40">No other members yet — invite first.</p>
          ) : (
            <div className="max-h-32 overflow-y-auto space-y-1">
              {members.slice(0, 25).map((m) => (
                <button
                  key={m.user_id}
                  type="button"
                  onClick={() => transfer(m.user_id)}
                  disabled={busy === `xfer-${m.user_id}`}
                  data-testid={`xfer-owner-${m.user_id}`}
                  className="w-full text-left p-2 rounded-xl bg-black/30 border border-white/10 hover:border-[#14b8a6]/40 flex items-center gap-2 disabled:opacity-50"
                >
                  <span className="text-[12px] font-bold truncate flex-1">{m.name || m.user_id}</span>
                  <span className="text-[9px] tracking-widest text-[#14b8a6]">HAND OFF →</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!isSandbox && (
        <div>
          <p className="text-[11px] text-white/70 mb-1.5">
            <i className="fas fa-user-tie mr-1 text-[#14b8a6]" />Assign manager
            {managerId && (
              <button
                type="button"
                onClick={() => assignMgr(null)}
                disabled={busy === "mgr-clear"}
                data-testid="league-clear-manager"
                className="ml-2 text-[9px] tracking-widest text-[#14b8a6] hover:underline disabled:opacity-50"
              >
                CLEAR
              </button>
            )}
          </p>
          {members.length === 0 ? (
            <p className="text-[11px] text-white/40">No other members yet.</p>
          ) : (
            <select
              value={managerId || ""}
              onChange={(e) => assignMgr(e.target.value || null)}
              data-testid="league-manager-select"
              disabled={busy.startsWith("mgr-")}
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
            >
              <option value="">— No manager —</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name || m.user_id}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* DANGER ZONE — destructive actions. */}
      <div className="pt-3 mt-2 border-t border-red-500/20">
        <p className="text-[10px] tracking-[3px] text-red-400 font-bold mb-2">
          <i className="fas fa-triangle-exclamation mr-1" />DANGER ZONE
        </p>
        {isSandbox ? (
          <button
            type="button"
            onClick={handleReset}
            disabled={busy === "reset"}
            data-testid="league-reset-sandbox-btn"
            className="w-full rounded-xl bg-[#14b8a6]/15 border border-[#14b8a6]/40 text-white text-xs tracking-widest font-bold py-2.5 hover:bg-[#14b8a6]/25 disabled:opacity-50"
          >
            <i className="fas fa-rotate mr-1" />{busy === "reset" ? "RESETTING…" : "RESET SANDBOX"}
          </button>
        ) : (
          <div className="space-y-2">
            <p className="text-[11px] text-white/60 leading-snug">
              Type the league name <b className="text-white">{league.name}</b> below to
              enable the delete button. This wipes all tournaments, applications,
              and challenges — and cannot be undone.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`Type "${league.name}"`}
              data-testid="league-delete-confirm-input"
              className="w-full bg-black/40 border border-red-500/30 rounded-xl px-3 py-2 text-white text-sm focus:border-red-500/70 outline-none"
            />
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy === "delete" || confirmText.trim() !== league.name}
              data-testid="league-delete-btn"
              className="w-full rounded-xl bg-red-500/20 border border-red-500/50 text-red-200 text-xs tracking-widest font-bold py-2.5 hover:bg-red-500/30 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className="fas fa-trash mr-1" />{busy === "delete" ? "DELETING…" : "DELETE LEAGUE"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function UpgradeLeagueToProCta({ league, onToast }) {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const res = await upgradeLeagueToPro(league.league_id, window.location.origin);
      if (res?.url) window.location.href = res.url;
      else onToast?.("Couldn't start checkout — retry.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't start checkout.");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="league-upgrade-to-pro-cta"
      className="mt-5 mb-5 rounded-2xl p-4 border-2 border-[#14b8a6]/60 bg-gradient-to-br from-[#14b8a6]/10 via-[#14b8a6]/10 to-[#14b8a6]/5"
    >
      <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
        <i className="fas fa-bolt mr-1" />UPGRADE THIS LEAGUE TO PRO · $9.99/MO
      </p>
      <p className="text-sm mt-2 text-white/85">
        Any member can power-up <b>{league.name}</b> to PRO. You'll unlock
        gold trim, AI banners, custom badges, prize-pool perks — and as a
        bonus you'll get <b>PRO Player status with the blue verified ✓</b>.
      </p>
      <button
        type="button"
        onClick={start}
        disabled={busy}
        data-testid="league-upgrade-to-pro-btn"
        className="mt-3 rounded-2xl bg-[#14b8a6] text-black font-bold px-5 py-2.5 text-xs tracking-widest hover:brightness-110 disabled:opacity-60"
      >
        {busy ? "OPENING CHECKOUT…" : "🔓 UPGRADE LEAGUE → PRO"}
      </button>
    </div>
  );
}


/**
 * CancelLeagueProCta — owner-only. Cancels the PRO upgrade on this specific
 * league, dropping it back to the FREE tier. Does NOT touch the owner's
 * personal PRO Player status (those are billed separately and the player
 * uses the dashboard CANCEL MEMBERSHIP button to drop their own tier).
 */
function CancelLeagueProCta({ league, onToast, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isGrace = !!league.pro_pending_cancel;
  const graceExp = league.pro_grace_expires_at ? new Date(league.pro_grace_expires_at) : null;
  const daysLeft = graceExp ? Math.max(0, Math.ceil((graceExp.getTime() - Date.now()) / 86400000)) : null;
  const cancel = async () => {
    if (!window.confirm(
      `Cancel PRO LEAGUE on "${league.name}"?\n\n` +
      "Gold trim, AI banners, tournaments and prize-pool perks stay active " +
      "for 7 more days, then this league drops back to the FREE tier. Members " +
      "stay enrolled. You can re-upgrade any time from inside the league."
    )) return;
    setBusy(true);
    try {
      const r = await cancelMembership({ kind: "league", league_id: league.league_id });
      onToast?.(r?.message || "PRO League cancelled — perks active 7 more days.");
      onChanged?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't cancel — try again.");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="league-cancel-pro-cta"
      className={`mt-5 mb-5 rounded-2xl p-4 border flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between ${
        isGrace ? "border-[#14b8a6]/70 bg-[#14b8a6]/10" : "border-[#14b8a6]/40 bg-[#14b8a6]/5"
      }`}
    >
      <div className="min-w-0">
        <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
          <i className="fas fa-crown mr-1" />
          {isGrace ? "PRO LEAGUE · CANCEL PENDING" : "PRO LEAGUE ACTIVE"}
        </p>
        <p className="text-[11px] text-white/70 mt-1">
          {isGrace ? (
            <>
              Perks expire in <b className="text-[#14b8a6]">{daysLeft} day{daysLeft === 1 ? "" : "s"}</b>.
              {" "}Re-up any time from the upgrade CTA to keep this league PRO.
            </>
          ) : (
            <>Drops <b className="text-white">{league.name}</b> back to FREE after a 7-day grace — you can re-upgrade any time.</>
          )}
        </p>
      </div>
      {!isGrace && (
        <button
          type="button"
          onClick={cancel}
          disabled={busy}
          data-testid="league-cancel-pro-btn"
          className="px-4 py-2 rounded-xl text-[10px] tracking-widest font-bold bg-[#14b8a6]/15 border border-[#14b8a6]/50 text-[#14b8a6] hover:bg-[#14b8a6]/25 disabled:opacity-50 whitespace-nowrap"
        >
          {busy ? "CANCELLING…" : "CANCEL PRO LEAGUE"}
        </button>
      )}
    </div>
  );
}


function ProInviteGenerator({ leagueId, onToast }) {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const make = async () => {
    setBusy(true);
    try {
      const r = await createInvite(leagueId);
      const url = `${window.location.origin}/invite/${r.token}`;
      setLink(url);
      try { await navigator.clipboard?.writeText(url); onToast?.("📋 Invite link copied — share it once."); }
      catch { onToast?.("✅ Invite link generated."); }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not generate invite.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#14b8a6]/30">
      <button
        onClick={make}
        disabled={busy}
        data-testid="pro-generate-invite-btn"
        className="rounded-2xl bg-[#14b8a6] text-black font-bold px-4 py-2 text-xs tracking-widest hover:brightness-110 disabled:opacity-50"
      >
        {busy ? "GENERATING..." : "🔗 GENERATE 1-CLICK INVITE"}
      </button>
      {link && (
        <div className="mt-2 flex items-center gap-2 glass rounded-xl p-2">
          <input
            readOnly
            value={link}
            data-testid="pro-invite-link"
            className="flex-1 bg-transparent text-xs text-[#14b8a6] font-mono outline-none truncate"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={() => { navigator.clipboard?.writeText(link); onToast?.("📋 Copied."); }}
            className="text-xs text-[#14b8a6] hover:text-white px-2"
          >
            COPY
          </button>
        </div>
      )}
      <p className="text-[10px] text-[#14b8a6]/60 mt-1.5">
        One-shot link — burns after first redeem. Skip the application step entirely.
      </p>
    </div>
  );
}


function InviteHqWidget({ league, onToast }) {
  const [open, setOpen] = useState(false);
  const [hqs, setHqs] = useState([]);
  const [q, setQ] = useState("");
  const [sentIds, setSentIds] = useState({});

  useEffect(() => {
    if (!open) return;
    fetchHqs({ game: league?.game, limit: 60 }).then(setHqs).catch(() => setHqs([]));
  }, [open, league?.game]);

  const send = async (teamId) => {
    setSentIds((p) => ({ ...p, [teamId]: "busy" }));
    try {
      await inviteHqToLeague(league.league_id, teamId);
      setSentIds((p) => ({ ...p, [teamId]: "sent" }));
      onToast?.("📨 Invite sent to team captain's inbox.");
    } catch (e) {
      setSentIds((p) => ({ ...p, [teamId]: "err" }));
      onToast?.(e?.response?.data?.detail || "Couldn't invite.");
    }
  };

  const filtered = hqs.filter((h) =>
    !q || (h.name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="mt-3 pt-3 border-t border-[#14b8a6]/30" data-testid="invite-hq-widget">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="invite-hq-toggle"
        className="rounded-2xl bg-[#14b8a6] text-black font-bold px-4 py-2 text-xs tracking-widest hover:brightness-110"
      >
        {open ? "× CLOSE TEAM INVITER" : "🏰 INVITE AN ENTIRE TEAM"}
      </button>
      {open && (
        <div className="mt-3 glass rounded-2xl p-3 border border-[#14b8a6]/40 space-y-2">
          <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold">
            FIND TEAM TO INVITE
          </p>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search team name…"
            data-testid="invite-hq-search"
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
          />
          <div className="max-h-64 overflow-y-auto space-y-1.5">
            {filtered.length === 0 ? (
              <p className="text-[11px] text-white/40 text-center py-3">
                No teams found for {league.game}.
              </p>
            ) : filtered.map((h) => {
              const state = sentIds[h.team_id];
              return (
                <div
                  key={h.team_id}
                  data-testid={`invite-hq-row-${h.team_id}`}
                  className="flex items-center gap-2 p-2 rounded-xl bg-black/30 border border-white/10"
                >
                  <AvatarChip avatarId={h.avatar_id} pfpUrl={h.pfp_url} size={28} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold truncate">{h.name}</p>
                    <p className="text-[10px] text-[#a3a3ac]">
                      {h.region || "—"} · {h.member_count} members · {h.competitive_level || "casual"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => state !== "sent" && send(h.team_id)}
                    disabled={state === "sent" || state === "busy"}
                    data-testid={`invite-hq-send-${h.team_id}`}
                    className={`text-[10px] tracking-widest px-3 py-1.5 rounded-lg ${
                      state === "sent"
                        ? "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
                        : "btn-neon"
                    }`}
                  >
                    {state === "sent" ? "✓ SENT" : state === "busy" ? "..." : "INVITE"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}



export function LeagueDetailModal({ leagueId, onClose, onToast, onRequireAuth, initialTab }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [apps, setApps] = useState([]);
  const [teams, setTeams] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [tours, setTours] = useState([]);
  const [badges, setBadges] = useState([]);
  const [tab, setTab] = useState(initialTab || "overview");
  const [busy, setBusy] = useState(false);
  const [decideBusyId, setDecideBusyId] = useState("");
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [showTourForm, setShowTourForm] = useState(false);
  const [shareBadge, setShareBadge] = useState(null);
  const [expandedTour, setExpandedTour] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [moderationOpen, setModerationOpen] = useState(false);
  const [teamInviteLink, setTeamInviteLink] = useState({});      // { team_id: url }
  const [teamInviteBusyId, setTeamInviteBusyId] = useState("");
  const [removeBusyKey, setRemoveBusyKey] = useState("");        // `${team_id}:${user_id}`
  const [editingTeam, setEditingTeam] = useState(null);
  const [showAddPickerFor, setShowAddPickerFor] = useState("");  // team_id or ""
  const [addBusyKey, setAddBusyKey] = useState("");
  const [challengeFromTeam, setChallengeFromTeam] = useState(null);  // own team initiating
  const [challengeToTeam, setChallengeToTeam] = useState(null);      // opponent team
  const [upcoming, setUpcoming] = useState([]);
  const [resultChallenge, setResultChallenge] = useState(null);      // accepted match → enter score
  const [leagueDmTarget, setLeagueDmTarget] = useState(null);        // {league_id, league_name, league_slug}
  const [joinReqBusyId, setJoinReqBusyId] = useState("");
  const [joinReqPending, setJoinReqPending] = useState({});         // { team_id: true }

  const load = useCallback(async () => {
    if (!leagueId) return;
    try {
      const d = await fetchLeague(leagueId);
      setData(d);
      const [t, tr, bd, ap, ug, liveStreams] = await Promise.all([
        fetchTeams(leagueId).catch(() => []),
        fetchTournaments(leagueId).catch(() => []),
        fetchBadges().catch(() => []),
        fetchAvailablePlayers(leagueId).catch(() => []),
        fetchUpcomingGames(leagueId).catch(() => []),
        // Phase 60 — pull live streams so the upcoming-game cards can
        // overlay a 🔴 LIVE pill and link straight to the broadcast.
        listLeagueStreams(leagueId).catch(() => ({ streams: [] })),
      ]);
      setTeams(t);
      setTours(tr);
      setBadges(bd);
      setAvailablePlayers(ap);
      const liveByChallenge = {};
      (liveStreams?.streams || []).forEach((s) => {
        liveByChallenge[s.challenge_id] = s;
      });
      setUpcoming((ug || []).map((g) => {
        const live = liveByChallenge[g.challenge_id];
        return live
          ? { ...g, is_live: true, stream_url: live.stream_url, stream_title: live.title }
          : g;
      }));
      if (d.is_owner) {
        const a = await fetchLeagueApplications(leagueId).catch(() => []);
        setApps(a);
      }
    } catch {
      onToast && onToast("Could not load league.");
      onClose();
    }
  }, [leagueId, onClose, onToast]);

  useEffect(() => { load(); }, [load]);

  if (!leagueId) return null;
  if (!data) {
    return (
      <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-md p-4 flex items-center justify-center" onClick={onClose}>
        <div className="loader-ring" />
      </div>
    );
  }

  const apply = async () => {
    if (!user) { onRequireAuth?.(); return; }
    setBusy(true);
    try {
      await applyToLeague(leagueId);
      onToast?.(`✅ Applied to ${data.name}. Owner notified.`);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Application failed.");
    } finally { setBusy(false); }
  };

  const decide = async (appId, action) => {
    setDecideBusyId(appId);
    try {
      await decideApplication(appId, action);
      onToast?.(action === "accept" ? "✅ Added to league." : "❌ Rejected.");
      await load();
    } catch { onToast?.("Decision failed."); }
    finally { setDecideBusyId(""); }
  };

  const generateTeamInvite = async (teamId) => {
    setTeamInviteBusyId(teamId);
    try {
      const r = await createTeamInvite(teamId);
      const url = `${window.location.origin}/team-invite/${r.token}`;
      setTeamInviteLink((prev) => ({ ...prev, [teamId]: url }));
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
      onToast?.(`🔗 Invite link copied to clipboard.`);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not create invite link.");
    } finally { setTeamInviteBusyId(""); }
  };

  const kickMember = async (teamId, userId, displayName) => {
    if (!window.confirm(`Remove ${displayName || "this player"} from the team?`)) return;
    const key = `${teamId}:${userId}`;
    setRemoveBusyKey(key);
    try {
      await removeTeamMember(teamId, userId);
      onToast?.(`👋 ${displayName || "Player"} removed.`);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not remove player.");
    } finally { setRemoveBusyKey(""); }
  };

  const inviteToMailbox = async (teamId, userId, displayName) => {
    const key = `${teamId}:${userId}`;
    setAddBusyKey(key);
    try {
      const r = await inviteToTeamMailbox(teamId, userId);
      onToast?.(
        r.already_pending
          ? `📬 ${displayName || "Player"} already has a pending invite.`
          : `📬 Invite sent to ${displayName || "player"}'s inbox.`
      );
      setShowAddPickerFor("");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not send invite.");
    } finally { setAddBusyKey(""); }
  };

  const requestJoin = async (team) => {
    if (!user) { onRequireAuth?.(); return; }
    setJoinReqBusyId(team.team_id);
    try {
      const r = await requestToJoinTeam(team.team_id);
      setJoinReqPending((prev) => ({ ...prev, [team.team_id]: true }));
      onToast?.(
        r.already_pending
          ? `📬 You already have a pending request to join ${team.name}.`
          : `📬 Request sent — ${team.name}'s manager will decide in their inbox.`
      );
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't send request.");
    } finally { setJoinReqBusyId(""); }
  };

  const filledRatio = ((data.members?.length || 1) / data.spots_total) * 100;
  const pendingApps = apps.filter((a) => a.status === "pending");
  const memberIds = data.members || [];
  const avgLeagueElo = data.members_detail?.length
    ? Math.round(data.members_detail.reduce((s, m) => s + (m.elo || 1200), 0) / data.members_detail.length)
    : 0;

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md p-2 sm:p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="league-detail-backdrop"
    >
      <div
        className={`glass rounded-3xl w-full max-w-4xl max-h-[94vh] max-h-[94dvh] overflow-y-auto ${
          data.league_type === "PRO"
            ? "border-2 border-[#14b8a6]/60 shadow-[0_0_36px_rgba(250,204,21,0.35)]"
            : "neon-glow"
        }`}
        onClick={(e) => e.stopPropagation()}
        data-testid="league-detail-modal"
      >
        {/* PRO banner — full-bleed at the top */}
        {data.league_type === "PRO" && data.banner_url && (
          <img
            src={data.banner_url}
            alt=""
            data-testid="league-detail-banner"
            className="w-full h-40 md:h-56 object-cover border-b border-[#14b8a6]/30"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="p-4 sm:p-7">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-5">
          <div className="min-w-0 md:flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-block px-3 py-1 text-[10px] font-bold tracking-[3px] rounded-full ${
                  data.league_type === "PRO"
                    ? "bg-[#14b8a6] text-black"
                    : "bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/40"
                }`}
              >
                {data.league_type === "PRO" ? "👑 PRO LEAGUE" : "CASUAL"}
              </span>
              {data.tier && (
                <span
                  data-testid="league-detail-tier"
                  className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-[3px] bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/50"
                >
                  {data.tier === 3 ? "👑 GOD MODE"
                    : data.tier === 2 ? "⚔ WAR ROOM"
                    : "🟢 SPAWN ZONE"}
                </span>
              )}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display grad-text mt-3 leading-tight flex items-center gap-3 break-words">
              {data.league_type === "PRO" && (
                <span data-testid="league-detail-pro-badge" className="text-[#14b8a6] text-3xl md:text-4xl flex-shrink-0">
                  {data.pro_badge || "👑"}
                </span>
              )}
              <span className="min-w-0 break-words">{data.name}</span>
            </h2>
            <p className="text-[#a3a3ac] mt-1 flex items-center gap-2 text-sm">
              <i className="fas fa-gamepad text-[#14b8a6]" /> {data.game}
            </p>
            {data.slug && (
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(`leaguehub.co/${data.slug.trim()}`);
                  onToast?.("🔗 Handle copied");
                }}
                data-testid="league-handle-pill"
                className="mt-2 inline-flex items-center gap-2 max-w-full px-3 py-1 rounded-full bg-black/50 border border-[#14b8a6]/50 hover:border-[#14b8a6] text-xs font-bold"
              >
                <i className="fas fa-link text-[#14b8a6] flex-shrink-0" />
                <span className="text-white/60 hidden sm:inline">leaguehub.co/</span>
                <span className="text-[#14b8a6] truncate">{data.slug.trim()}</span>
                <i className="fas fa-copy text-white/40 ml-1 flex-shrink-0" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-end gap-1.5 md:gap-2 flex-wrap md:flex-nowrap md:flex-shrink-0">
            {data.slug && (
              <button
                onClick={async () => {
                  const url = `${window.location.origin}/${data.slug}`;
                  try { await navigator.clipboard?.writeText(url); onToast?.("📋 Public link copied — share away."); }
                  catch { onToast?.(url); }
                }}
                data-testid="league-detail-share-btn"
                title="Copy public link"
                aria-label="Copy public link"
                className="btn-ghost rounded-2xl px-3 py-2 text-xs tracking-widest"
              >
                <i className="fas fa-share-alt md:mr-1" /> <span className="hidden md:inline">SHARE</span>
              </button>
            )}
            {data.is_owner && (
              <button
                onClick={() => setEditOpen(true)}
                data-testid="league-detail-edit-btn"
                className="btn-ghost rounded-2xl px-3 py-2 text-xs tracking-widest"
                title="Edit league"
                aria-label="Edit league"
              >
                <i className="fas fa-pen md:mr-1" /> <span className="hidden md:inline">EDIT</span>
              </button>
            )}
            {(data.is_owner || data.is_co_admin || data.is_moderator) && (
              <button
                onClick={() => setModerationOpen(true)}
                data-testid="league-detail-moderate-btn"
                className="btn-ghost rounded-2xl px-3 py-2 text-xs tracking-widest"
                title="Moderate disputes"
                aria-label="Moderate disputes"
              >
                <i className="fas fa-gavel md:mr-1" /> <span className="hidden md:inline">MODERATE</span>
              </button>
            )}
            <button
              onClick={onClose}
              data-testid="league-detail-close"
              className="text-white/60 hover:text-white text-2xl px-2"
              aria-label="Close"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="glass rounded-2xl p-3">
            <p className="text-[9px] tracking-widest text-[#14b8a6]/80">SPOTS</p>
            <p className="text-base font-display mt-1">
              <span className="text-[#14b8a6]">{data.members?.length || 1}</span>/{data.spots_total}
            </p>
            <div className="h-1 rounded-full bg-black/40 overflow-hidden mt-2">
              <div className="h-full" style={{ width: `${filledRatio}%`, background: "linear-gradient(90deg, #14b8a6, #14b8a6)" }} />
            </div>
          </div>
          <div className="glass rounded-2xl p-3">
            <p className="text-[9px] tracking-widest text-[#14b8a6]/80">LEAGUE MMR</p>
            <p className="text-base font-display mt-1 text-[#14b8a6]">{avgLeagueElo}</p>
          </div>
          <div className="glass rounded-2xl p-3">
            <p className="text-[9px] tracking-widest text-[#14b8a6]/80">TEAMS / TOURS</p>
            <p className="text-base font-display mt-1">
              <span className="text-[#14b8a6]">{teams.length}</span> / <span className="text-[#14b8a6]">{tours.length}</span>
            </p>
          </div>
        </div>

        {data.description && (
          <div className="glass rounded-2xl p-3 mb-5">
            <p className="text-[9px] tracking-widest text-[#14b8a6] mb-1">DESCRIPTION</p>
            <p className="text-sm text-white/85 whitespace-pre-line">{data.description}</p>
          </div>
        )}

        {data.availability?.slots && Object.keys(data.availability.slots).length > 0 && (
          <div
            data-testid="league-availability"
            className="glass rounded-2xl p-3 mb-5 border border-[#14b8a6]/30"
          >
            <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold mb-2">
              <i className="fas fa-calendar-check mr-1" />LEAGUE PLAY WINDOWS
              {data.availability.timezone && data.availability.timezone !== "UTC" && (
                <span className="text-white/40 ml-2 normal-case">({data.availability.timezone})</span>
              )}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {DAY_ORDER.map((d) => {
                const slots = data.availability.slots[d] || [];
                if (!slots.length) return null;
                return (
                  <span
                    key={d}
                    title={`${d.toUpperCase()} · ${slots.map((s) => SLOT_LABELS[s] || s).join(", ")}`}
                    className="inline-flex items-center gap-1 text-[10px] tracking-widest px-2 py-1 rounded-md bg-[#14b8a6]/10 text-[#14b8a6] border border-[#14b8a6]/30"
                  >
                    <span className="font-bold">{d.toUpperCase()}</span>
                    <span className="text-[#14b8a6]/80">·</span>
                    <span className="text-[#14b8a6]/90">
                      {slots.map((s) => SLOT_LABELS[s] || s).join("/")}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* PRO PERKS panel — only shown for PRO leagues with perks set */}
        {data.league_type === "PRO" && (data.pro_perks?.length > 0 || data.is_owner) && (
          <div data-testid="league-detail-pro-perks"
               className="rounded-2xl p-4 mb-5 border-2 border-[#14b8a6]/60 bg-[#14b8a6]/5">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">👑 PRO PERKS</p>
            {data.pro_perks?.length > 0 ? (
              <ul className="space-y-1.5">
                {data.pro_perks.map((p, i) => (
                  <li key={i} className="text-sm text-[#14b8a6]/90 flex items-start gap-2">
                    <span className="text-[#14b8a6] leading-none mt-1">★</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-[#14b8a6]/60">Owner hasn't listed perks yet.</p>
            )}
            {data.is_owner && (
              <>
                <ProInviteGenerator leagueId={data.league_id} onToast={onToast} />
                <InviteHqWidget league={data} onToast={onToast} />
                <LeagueDmLauncher league={data} onPick={(l) => setLeagueDmTarget(l)} onToast={onToast} />
              </>
            )}
          </div>
        )}

        {/* GOVERNANCE — every league owner gets transfer/manager/delete
            controls regardless of PRO status. Sandbox leagues render a
            different banner inside the panel (no transfer, no delete,
            just Reset Sandbox). */}
        {data.is_owner && (
          <div data-testid="league-detail-governance-wrap" className="mb-5 rounded-2xl border border-[#14b8a6]/30 bg-black/30 p-4">
            <GovernancePanel league={data} onChanged={load} onClose={onClose} onToast={onToast} />
          </div>
        )}

        {/* Casual league — surface the upgrade-to-PRO CTA to every member. */}
        {data.is_member && data.league_type !== "PRO" && (
          <UpgradeLeagueToProCta league={data} onToast={onToast} />
        )}

        {/* PRO league — owner sees a cancel-membership affordance. */}
        {data.is_owner && data.league_type === "PRO" && (
          <CancelLeagueProCta league={data} onToast={onToast} onChanged={load} />
        )}

        {/* CTA — always visible so the user always knows their state */}
        <div className="mb-5">
          {data.is_owner ? (
            <div data-testid="league-detail-owner-cta"
                 className="w-full py-3 rounded-2xl bg-[#14b8a6]/10 border border-[#14b8a6]/40 text-[#14b8a6] font-bold text-center">
              👑 YOU'RE THE OWNER • Open the APPLICATIONS tab to manage joiners.
            </div>
          ) : data.is_member ? (
            <div data-testid="league-detail-member-cta"
                 className="w-full py-3 rounded-2xl bg-[#14b8a6]/10 border border-[#14b8a6]/40 text-[#14b8a6] font-bold text-center">
              ✅ YOU'RE IN. Hit the TEAMS tab to join or build a team.
            </div>
          ) : data.my_application?.status === "pending" ? (
            <button disabled data-testid="league-detail-applied"
                    className="w-full py-3 rounded-2xl bg-white/10 border border-[#14b8a6]/40 text-white font-bold">
              ⏳ APPLICATION PENDING
            </button>
          ) : data.my_application?.status === "rejected" ? (
            <button disabled className="w-full py-3 rounded-2xl bg-white/10 border border-[#14b8a6]/40 text-[#14b8a6] font-bold">
              REJECTED
            </button>
          ) : (
            <button
              onClick={apply}
              disabled={busy || data.spots_open <= 0}
              data-testid="league-detail-apply-btn"
              className="w-full py-3 rounded-2xl btn-neon disabled:opacity-40"
            >
              {busy ? "APPLYING..." : data.spots_open <= 0 ? "LEAGUE FULL" : "🎯 APPLY TO LEAGUE"}
            </button>
          )}
        </div>

        <BoostBar leagueId={data.league_id} onToast={onToast} />

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-white/10 pb-3 flex-wrap">
          {[
            { id: "overview", label: `👥 MEMBERS (${data.members_detail?.length || 0})` },
            { id: "teams", label: `🛡️ TEAMS (${teams.length})` },
            { id: "ladder", label: `🏅 LADDER` },
            { id: "tournaments", label: `🏆 TOURNAMENTS (${tours.length})` },
            { id: "chat", label: `💬 CHAT` },
            { id: "heatmap", label: `🔥 HEATMAP${data.league_type === "PRO" ? "" : " 🔒"}` },
            { id: "badges", label: `🎖️ BADGES (${badges.length})` },
            { id: "clips", label: `🎬 CLIPS` },
            ...(data.is_owner ? [{ id: "applications", label: `📨 APPLICATIONS (${apps.length})`, badge: pendingApps.length }] : []),
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`league-tab-${t.id}`}
              className={`px-3 py-2 rounded-2xl text-[11px] font-bold tracking-widest relative ${
                tab === t.id ? "btn-neon" : "bg-white/5 text-white/70 hover:text-white"
              }`}
            >
              {t.label}
              {t.badge ? (
                <span className="absolute -top-2 -right-2 bg-[#14b8a6] text-black text-[10px] font-bold rounded-full px-1.5 py-0.5">
                  {t.badge}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        {/* MEMBERS */}
        {tab === "overview" && (
          <div>
            {data.is_owner && (
              <div className="mb-4">
                <InvitePlayerWidget
                  leagueId={leagueId}
                  currentMemberIds={memberIds}
                  currentUserId={user?.user_id}
                  ownerUserId={data.owner_user_id}
                  onInvited={load}
                  onToast={onToast}
                />
              </div>
            )}
            {data.members_detail?.length ? (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="league-members-grid">
                {data.members_detail.map((m) => (
                  <div key={m.user_id} data-testid={`league-member-${m.user_id}`} className="glass rounded-2xl p-3 flex items-center gap-3">
                    <AvatarChip avatarId={m.avatar_id} pfpUrl={m.pfp_url} size={48} />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate">{m.name}</p>
                      <p className="text-xs text-[#a3a3ac]">MMR {m.elo} • {(m.user_type || "casual").toUpperCase()}</p>
                    </div>
                    {m.user_id === data.owner_user_id && (
                      <span className="text-[9px] tracking-widest bg-[#14b8a6] text-black px-2 py-1 rounded-full font-bold">OWNER</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/60 text-center py-6">No members yet.</p>
            )}
          </div>
        )}

        {/* TEAMS */}
        {tab === "teams" && (
          <div>
            {/* Phase 60 — let any user with a standalone team bring it in,
                regardless of whether they're the owner. Component
                self-hides when the user has nothing to attach. */}
            <AttachTeamPicker
              user={user}
              league={data}
              onToast={onToast}
              onAttached={() => load()}
            />
            {data.is_owner && (
              <div className="mb-4">
                {showTeamForm ? (
                  <TeamForm
                    leagueId={leagueId}
                    members={data.members_detail || []}
                    onCreated={() => { setShowTeamForm(false); load(); }}
                    onCancel={() => setShowTeamForm(false)}
                    onToast={onToast}
                  />
                ) : (
                  <button
                    onClick={() => setShowTeamForm(true)}
                    data-testid="open-team-form"
                    className="btn-neon w-full py-3 rounded-2xl"
                  >
                    + CREATE TEAM
                  </button>
                )}
              </div>
            )}
            {!data.is_owner && data.is_member && teams.length > 0 && (
              <div className="mb-4 glass rounded-2xl p-3 text-[11px] text-white/55" data-testid="member-join-hint">
                <i className="fas fa-circle-info text-[#14b8a6] mr-1.5" />
                You're in this league — tap <b className="text-[#14b8a6]">REQUEST TO JOIN</b> on any team card below, or use the attach panel above to bring your own team in.
              </div>
            )}
            {teams.length === 0 ? (
              <p className="text-white/60 text-center py-6" data-testid="no-teams">
                No teams yet. {data.is_owner ? "Tap + CREATE TEAM above to add the first." : "The league owner hasn't added any teams yet."}
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="teams-list">
                {[...teams].sort((a, b) => (b.mmr || 0) - (a.mmr || 0)).map((t, idx) => {
                  const isManager = !!user && user.user_id === t.captain_user_id;
                  const isMember = !!user && (t.member_user_ids || t.members?.map((m) => m.user_id) || []).some((uid) => (typeof uid === "string" ? uid : uid?.user_id) === user.user_id);
                  const myManagedTeamsHere = teams.filter((x) => x.captain_user_id === user?.user_id && x.team_id !== t.team_id);
                  const canChallenge = !!user && !isManager && myManagedTeamsHere.length > 0;
                  const canRequestJoin = !!user && !isManager && !isMember;
                  const reqPending = !!joinReqPending[t.team_id];
                  return (
                  <div key={t.team_id} data-testid={`team-card-${t.team_id}`} className="card-poke glass rounded-2xl p-4 relative">
                    {idx === 0 && teams.length > 1 && (
                      <span className="absolute top-2 right-2 text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full bg-[#14b8a6] text-black">
                        TOP MMR
                      </span>
                    )}
                    <div className="flex items-center gap-3 mb-2">
                      <AvatarChip avatarId={t.avatar_id} pfpUrl={t.pfp_url} size={48} />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold truncate">{t.name}</p>
                        <p className="text-xs text-[#a3a3ac]">
                          {t.members?.length || 0} member{(t.members?.length || 0) === 1 ? "" : "s"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[9px] tracking-widest text-[#14b8a6]">MMR</p>
                        <p className="font-display text-xl grad-text leading-none" data-testid={`team-mmr-${t.team_id}`}>
                          {t.mmr || 0}
                        </p>
                      </div>
                    </div>
                    {t.social_link && (
                      <a
                        href={t.social_link.startsWith("http") ? t.social_link : `https://${t.social_link}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`team-social-${t.team_id}`}
                        className="text-[10px] text-[#14b8a6] hover:underline truncate block"
                      >
                        <i className="fas fa-link mr-1" />{t.social_link}
                      </a>
                    )}
                    {t.availability?.slots && Object.keys(t.availability.slots).length > 0 && (
                      <div
                        data-testid={`team-availability-${t.team_id}`}
                        className="mt-2 space-y-1"
                      >
                        <span className="text-[9px] tracking-widest text-[#14b8a6]">
                          <i className="fas fa-calendar-check mr-0.5" />FREE TO PLAY
                          {t.availability.timezone && t.availability.timezone !== "UTC" && (
                            <span className="text-white/40 ml-1">({t.availability.timezone})</span>
                          )}
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {DAY_ORDER.map((d) => {
                            const slots = t.availability.slots[d] || [];
                            if (!slots.length) return null;
                            return (
                              <span
                                key={d}
                                title={`${d.toUpperCase()} · ${slots.map((s) => SLOT_LABELS[s] || s).join(", ")}`}
                                className="inline-flex items-center gap-1 text-[9px] tracking-widest px-1.5 py-0.5 rounded-md bg-[#14b8a6]/10 text-[#14b8a6] border border-[#14b8a6]/30"
                              >
                                <span className="font-bold">{d.toUpperCase()}</span>
                                <span className="text-[#14b8a6]/80">·</span>
                                <span className="text-[#14b8a6]/90">
                                  {slots.map((s) => SLOT_LABELS[s] || s).join("/")}
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {canChallenge && (
                      <button
                        onClick={() => {
                          // Default the from-team to whichever managed team this user owns in this league.
                          setChallengeFromTeam(myManagedTeamsHere[0]);
                          setChallengeToTeam(t);
                        }}
                        data-testid={`team-challenge-btn-${t.team_id}`}
                        className="mt-2 w-full text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                      >
                        <i className="fas fa-bolt mr-1" />CHALLENGE
                      </button>
                    )}
                    {canRequestJoin && (
                      <button
                        type="button"
                        onClick={() => requestJoin(t)}
                        disabled={reqPending || joinReqBusyId === t.team_id}
                        data-testid={`team-request-join-btn-${t.team_id}`}
                        className={`mt-2 w-full text-[10px] tracking-widest px-3 py-1.5 rounded-lg border transition ${
                          reqPending
                            ? "bg-white/5 text-white/40 border-white/15 cursor-not-allowed"
                            : "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                        }`}
                      >
                        {joinReqBusyId === t.team_id
                          ? "..."
                          : reqPending
                            ? <><i className="fas fa-hourglass-half mr-1" />REQUEST PENDING</>
                            : <><i className="fas fa-user-plus mr-1" />REQUEST TO JOIN</>}
                      </button>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(t.members || []).slice(0, 8).map((m) => (
                        <span
                          key={m.user_id}
                          className="relative inline-block"
                          title={m.user_id === t.captain_user_id ? `${m.name} · MANAGER` : m.name}
                        >
                          <AvatarChip avatarId={m.avatar_id} pfpUrl={m.pfp_url} size={24} />
                          {m.user_id === t.captain_user_id && (
                            <i
                              className="fas fa-crown absolute -top-1 -right-1 text-[9px] text-[#14b8a6]"
                              data-testid={`team-manager-marker-${t.team_id}-${m.user_id}`}
                            />
                          )}
                        </span>
                      ))}
                    </div>
                    {t.badges?.length > 0 && (
                      <div data-testid={`team-badges-${t.team_id}`} className="mt-3 pt-2 border-t border-white/10 flex items-center gap-1 flex-wrap">
                        <span className="text-[9px] tracking-widest text-[#14b8a6] mr-1">🎖️</span>
                        {t.badges.slice(0, 6).map((b) => {
                          const tier = (b.tier || "bronze").toLowerCase();
                          const ring = tier === "diamond" ? "border-[#14b8a6]"
                            : tier === "gold" ? "border-[#14b8a6]"
                            : tier === "silver" ? "border-slate-400"
                            : "border-[#14b8a6]";
                          const txt = tier === "diamond" ? "text-[#14b8a6]"
                            : tier === "gold" ? "text-[#14b8a6]"
                            : tier === "silver" ? "text-slate-300"
                            : "text-[#14b8a6]0";
                          return (
                            <span key={b.code} title={`${b.name} — ${b.description || tier.toUpperCase()}`}
                                  className={`w-7 h-7 rounded-full border-2 ${ring} bg-black/50 flex items-center justify-center text-xs ${txt}`}>
                              <i className={`fas ${b.icon || "fa-medal"}`} />
                            </span>
                          );
                        })}
                        {t.badges.length > 6 && (
                          <span className="text-[10px] text-white/40 ml-1">+{t.badges.length - 6}</span>
                        )}
                      </div>
                    )}
                    {isManager && (
                      <div
                        data-testid={`team-manage-${t.team_id}`}
                        className="mt-3 pt-2 border-t border-[#14b8a6]/20 space-y-2"
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-[9px] font-bold tracking-[2px] text-[#14b8a6]">
                            <i className="fas fa-crown mr-1" />MANAGER CONTROLS
                          </span>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              onClick={() => setEditingTeam(t)}
                              data-testid={`team-edit-btn-${t.team_id}`}
                              className="text-[10px] tracking-widest px-3 py-1 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                            >
                              <i className="fas fa-pen mr-1" />EDIT
                            </button>
                            <button
                              onClick={() => setShowAddPickerFor(showAddPickerFor === t.team_id ? "" : t.team_id)}
                              data-testid={`team-add-btn-${t.team_id}`}
                              disabled={availablePlayers.length === 0}
                              className="text-[10px] tracking-widest px-3 py-1 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-40"
                              title={availablePlayers.length === 0 ? "No free agents in this league" : "Send a join invite to a league member's mailbox"}
                            >
                              <i className="fas fa-envelope mr-1" />INVITE
                            </button>
                            <button
                              onClick={() => generateTeamInvite(t.team_id)}
                              disabled={teamInviteBusyId === t.team_id}
                              data-testid={`team-invite-btn-${t.team_id}`}
                              className="text-[10px] tracking-widest px-3 py-1 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50"
                            >
                              {teamInviteBusyId === t.team_id ? "..." : "🔗 INVITE LINK"}
                            </button>
                          </div>
                        </div>
                        {teamInviteLink[t.team_id] && (
                          <div
                            data-testid={`team-invite-link-${t.team_id}`}
                            className="text-[10px] text-[#a3a3ac] bg-black/40 rounded-lg px-2 py-1 truncate select-all"
                          >
                            {teamInviteLink[t.team_id]}
                          </div>
                        )}
                        {showAddPickerFor === t.team_id && (
                          <div
                            data-testid={`team-add-picker-${t.team_id}`}
                            className="bg-black/40 rounded-lg p-2 border border-[#14b8a6]/30 space-y-1 max-h-44 overflow-y-auto"
                          >
                            <p className="text-[9px] tracking-widest text-[#14b8a6]">FREE AGENTS · invite to mailbox</p>
                            {availablePlayers.length === 0 ? (
                              <p className="text-[10px] text-white/50">No unteamed players in this league.</p>
                            ) : availablePlayers.map((p) => {
                              const akey = `${t.team_id}:${p.user_id}`;
                              return (
                                <div key={p.user_id} className="flex items-center justify-between gap-2 text-xs">
                                  <span className="flex items-center gap-2 min-w-0">
                                    <AvatarChip avatarId={p.avatar_id} pfpUrl={p.pfp_url} size={20} />
                                    <span className="truncate">{p.name}</span>
                                    <span className="text-[10px] text-white/40">MMR {p.elo || 1200}</span>
                                  </span>
                                  <button
                                    onClick={() => inviteToMailbox(t.team_id, p.user_id, p.name)}
                                    disabled={addBusyKey === akey}
                                    data-testid={`team-add-pick-${t.team_id}-${p.user_id}`}
                                    className="text-[10px] tracking-widest px-2 py-0.5 rounded bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50"
                                  >
                                    {addBusyKey === akey ? "..." : "INVITE"}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {(t.members || []).filter((m) => m.user_id !== t.captain_user_id).length > 0 && (
                          <div className="space-y-1">
                            {(t.members || [])
                              .filter((m) => m.user_id !== t.captain_user_id)
                              .map((m) => {
                                const key = `${t.team_id}:${m.user_id}`;
                                return (
                                  <div key={m.user_id} className="flex items-center justify-between gap-2 text-xs bg-black/30 rounded-lg px-2 py-1">
                                    <span className="flex items-center gap-2 min-w-0">
                                      <AvatarChip avatarId={m.avatar_id} pfpUrl={m.pfp_url} size={20} />
                                      <span className="truncate">{m.name}</span>
                                    </span>
                                    <button
                                      onClick={() => kickMember(t.team_id, m.user_id, m.name)}
                                      disabled={removeBusyKey === key}
                                      data-testid={`team-kick-${t.team_id}-${m.user_id}`}
                                      className="text-[10px] tracking-widest px-2 py-0.5 rounded bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50"
                                    >
                                      {removeBusyKey === key ? "..." : "REMOVE"}
                                    </button>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
            {/* Available players — league members not on any team yet */}
            {availablePlayers.length > 0 && (
              <div data-testid="available-players-section" className="mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold tracking-[3px] text-[#a3a3ac]">
                    <i className="fas fa-user-clock mr-1" />FREE AGENTS · {availablePlayers.length}
                  </h4>
                  <span className="text-[10px] text-white/40">looking to join a squad</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {availablePlayers.map((p) => (
                    <div
                      key={p.user_id}
                      data-testid={`available-player-${p.user_id}`}
                      className="glass rounded-xl px-3 py-2 flex items-center gap-2"
                    >
                      <AvatarChip avatarId={p.avatar_id} pfpUrl={p.pfp_url} size={32} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate">{p.name}</p>
                        <p className="text-[10px] text-[#a3a3ac]">
                          MMR {p.elo || 1200}{p.user_type === "pro" ? " · PRO" : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming games — accepted challenges in this league */}
            {upcoming.length > 0 && (
              <div data-testid="upcoming-games-section" className="mt-6 pt-4 border-t border-[#14b8a6]/30">
                <h4 className="text-xs font-bold tracking-[3px] text-[#14b8a6] mb-3">
                  <i className="fas fa-calendar-day mr-1" />UPCOMING GAMES · {upcoming.length}
                </h4>
                <div className="space-y-2">
                  {upcoming.map((g) => {
                    const when = g.scheduled_at ? new Date(g.scheduled_at) : null;
                    const whenStr = when ? when.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "TBD";
                    // Can the current user enter a result? Yes if they manage either team.
                    const canEnter = !!user && (
                      (g.from_team && (g.from_team.captain_user_id === user.user_id)) ||
                      (g.to_team && (g.to_team.captain_user_id === user.user_id)) ||
                      teams.some((t) => (t.team_id === g.from_team?.team_id || t.team_id === g.to_team?.team_id) && (t.captain_user_id === user.user_id || (t.captains || []).includes(user.user_id)))
                    );
                    return (
                      <div
                        key={g.challenge_id}
                        data-testid={`upcoming-game-${g.challenge_id}`}
                        className="glass rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3 border border-[#14b8a6]/30"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <AvatarChip avatarId={g.from_team?.avatar_id} pfpUrl={g.from_team?.pfp_url} size={36} />
                          <div className="min-w-0">
                            <p className="text-sm font-bold truncate">{g.from_team?.name || "TBD"}</p>
                            <p className="text-[10px] text-white/50">MMR {g.from_team?.mmr || 0}</p>
                          </div>
                        </div>
                        <span className="font-display text-xl grad-text">VS</span>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <AvatarChip avatarId={g.to_team?.avatar_id} pfpUrl={g.to_team?.pfp_url} size={36} />
                          <div className="min-w-0">
                            <p className="text-sm font-bold truncate">{g.to_team?.name || "TBD"}</p>
                            <p className="text-[10px] text-white/50">MMR {g.to_team?.mmr || 0}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {g.is_live && g.stream_url ? (
                            <a
                              href={g.stream_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`upcoming-live-link-${g.challenge_id}`}
                              className="inline-flex items-center gap-1 text-[9px] tracking-widest font-bold px-2 py-1 rounded-full bg-red-500/20 text-red-300 border border-red-500/45 hover:bg-red-500/30"
                              title="Watch live stream"
                            >
                              <i className="fas fa-circle text-red-500 animate-pulse" />LIVE
                              <i className="fas fa-up-right-from-square text-[8px] opacity-70" />
                            </a>
                          ) : (
                            <p className="text-[9px] tracking-widest text-[#14b8a6]">
                              {g.is_live ? "🔴 LIVE" : "SCHEDULED"}
                            </p>
                          )}
                          <p className="text-xs font-bold mt-1">{whenStr}</p>
                          {g.note && <p className="text-[10px] text-white/50 italic mt-1">"{g.note}"</p>}
                          {canEnter && (
                            <button
                              type="button"
                              onClick={() => setResultChallenge(g)}
                              data-testid={`upcoming-enter-result-${g.challenge_id}`}
                              className="mt-2 text-[10px] tracking-widest px-3 py-1 rounded-lg bg-[#14b8a6] text-black font-bold hover:brightness-110"
                            >
                              <i className="fas fa-flag-checkered mr-1" />ENTER RESULT
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* LADDER */}
        {tab === "ladder" && (
          <LeagueLadderTab slug={data.slug} onToast={onToast} />
        )}


        {/* TOURNAMENTS */}
        {tab === "tournaments" && (
          <div>
            {data.is_owner && (
              <div className="mb-4">
                {showTourForm ? (
                  <TournamentForm
                    leagueId={leagueId}
                    teams={teams}
                    onCreated={() => { setShowTourForm(false); load(); }}
                    onCancel={() => setShowTourForm(false)}
                    onToast={onToast}
                  />
                ) : (
                  <button
                    onClick={() => setShowTourForm(true)}
                    data-testid="open-tournament-form"
                    className="btn-neon w-full py-3 rounded-2xl"
                  >
                    + NEW TOURNAMENT
                  </button>
                )}
              </div>
            )}
            {tours.length === 0 ? (
              <p className="text-white/60 text-center py-6" data-testid="no-tournaments">
                No tournaments scheduled. {data.is_owner ? "Schedule your first above." : "Stay tuned."}
              </p>
            ) : (
              <div className="space-y-3" data-testid="tournaments-list">
                {tours.map((t) => (
                  <div key={t.tournament_id} data-testid={`tournament-card-${t.tournament_id}`} className="glass rounded-2xl p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-display text-lg grad-text">{t.name}</p>
                        <p className="text-xs text-[#a3a3ac]">
                          <i className="fas fa-calendar mr-1 text-[#14b8a6]" /> {t.start_date}
                          {" • "}{TOURNAMENT_STYLES.find(s => s.value === t.style)?.label || t.style}
                        </p>
                        {t.season && (
                          <p className="text-[10px] tracking-widest text-[#14b8a6] mt-1" data-testid={`tournament-season-${t.tournament_id}`}>
                            🗓️ SEASON • {t.season}
                          </p>
                        )}
                      </div>
                      <span className="text-[9px] tracking-widest px-2 py-1 rounded-full bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40">
                        {t.status?.toUpperCase() || "SCHEDULED"}
                      </span>
                    </div>
                    {t.description && <p className="text-xs text-white/70 mb-2">{t.description}</p>}
                    {t.teams?.length > 0 && (
                      <div>
                        <p className="text-[10px] tracking-widest text-[#14b8a6] mb-1">ENROLLED TEAMS</p>
                        <div className="flex flex-wrap gap-2">
                          {t.teams.map(team => (
                            <span key={team.team_id} className="inline-flex items-center gap-1.5 bg-black/40 border border-white/15 rounded-xl px-2 py-1 text-xs">
                              <AvatarChip avatarId={team.avatar_id} size={20} />
                              <span className="font-bold">{team.name}</span>
                              <span className="text-[#a3a3ac]">MMR {team.mmr}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => setExpandedTour(expandedTour === t.tournament_id ? null : t.tournament_id)}
                      data-testid={`tournament-toggle-bracket-${t.tournament_id}`}
                      className="mt-3 w-full btn-ghost rounded-xl py-2 text-[11px] tracking-widest"
                    >
                      <i className={`fas ${expandedTour === t.tournament_id ? "fa-chevron-up" : "fa-chevron-down"} mr-1`} />
                      {expandedTour === t.tournament_id ? "HIDE BRACKET" : "🥊 SHOW BRACKET"}
                    </button>
                    {expandedTour === t.tournament_id && (
                      <div className="mt-3">
                        <BracketView
                          tournament={t}
                          isOwner={!!data.is_owner}
                          onToast={onToast}
                          onTournamentChanged={load}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CHAT (in-league messaging) */}
        {tab === "chat" && (
          <div data-testid="chat-tab-content">
            <LeagueChat
              leagueId={data.league_id}
              isMember={!!(data.is_member || data.is_owner)}
              onToast={onToast}
            />
          </div>
        )}

        {/* HEATMAP (PRO-only stats view) */}
        {tab === "heatmap" && (
          <div data-testid="heatmap-tab-content">
            <LeagueHeatmap leagueId={leagueId} weeks={8} />
          </div>
        )}

        {/* BADGES (league achievement vault) */}
        {tab === "badges" && (
          <div data-testid="badges-tab-content">
            <p className="text-[10px] tracking-widest text-[#14b8a6] mb-3">🎖️ ACHIEVEMENT VAULT • EARN NEON, FLEX HARD</p>
            {badges.length === 0 ? (
              <p className="text-white/60 text-center py-6" data-testid="no-badges">
                No badges available yet.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="badges-grid">
                {badges.map((b) => {
                  const tier = (b.tier || "bronze").toLowerCase();
                  const ring = tier === "diamond" ? "border-[#14b8a6]"
                    : tier === "gold" ? "border-[#14b8a6]"
                    : tier === "silver" ? "border-slate-400"
                    : "border-[#14b8a6]";
                  const text = tier === "diamond" ? "text-[#14b8a6]"
                    : tier === "gold" ? "text-[#14b8a6]"
                    : tier === "silver" ? "text-slate-300"
                    : "text-[#14b8a6]0";
                  return (
                    <button
                      key={b.badge_id}
                      type="button"
                      onClick={() => setShareBadge(b)}
                      data-testid={`badge-${b.badge_id}`}
                      className={`glass rounded-2xl p-4 text-center cursor-pointer hover:border-[#14b8a6]/60 transition-all ${b.earned ? "" : "opacity-50 grayscale"}`}
                    >
                      <div className={`mx-auto w-16 h-16 rounded-full border-4 ${ring} flex items-center justify-center`}>
                        <i className={`fas ${b.icon || "fa-medal"} text-2xl ${text}`} />
                      </div>
                      <p className="mt-3 font-display text-xs tracking-widest">{b.name}</p>
                      <p className="text-[10px] text-white/60 mt-1 line-clamp-2">{b.description}</p>
                      <span className={`inline-block mt-2 text-[9px] tracking-widest ${text}`}>
                        {tier.toUpperCase()}
                      </span>
                      <span className="block mt-2 text-[9px] tracking-widest text-[#14b8a6]">📣 SHARE</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* CLIPS (Phase 60) — public read; casters/owners/co-admins post. */}
        {tab === "clips" && (
          <LeagueClipsPanel
            league={data}
            currentUserId={user?.user_id}
            canPost={!!(data.is_owner || data.is_co_admin || data.my_league_role === "caster")}
            isAdmin={!!(data.is_owner || data.is_co_admin)}
            onToast={onToast}
          />
        )}

        {/* APPLICATIONS (owner only) */}
        {tab === "applications" && data.is_owner && (
          apps.length === 0 ? (
            <p className="text-white/60 text-center py-6" data-testid="league-no-applications">
              No applications yet.
            </p>
          ) : (
            <div className="space-y-3" data-testid="league-applications-list">
              {apps.map((a) => (
                <div key={a.application_id} data-testid={`league-application-${a.application_id}`} className="glass rounded-2xl p-3 flex items-center gap-3">
                  <AvatarChip avatarId={a.user_avatar_id} pfpUrl={a.user_pfp_url} size={48} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{a.user_name}</p>
                    <p className="text-xs text-[#a3a3ac]">MMR {a.user_elo ?? "—"} • {(a.user_type || "casual").toUpperCase()}</p>
                  </div>
                  {a.status === "pending" ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => decide(a.application_id, "reject")}
                        disabled={decideBusyId === a.application_id}
                        data-testid={`league-reject-${a.application_id}`}
                        className="px-3 py-2 rounded-xl text-xs bg-black/50 border border-[#14b8a6]/50 text-[#14b8a6] disabled:opacity-50 font-bold"
                      >REJECT</button>
                      <button
                        onClick={() => decide(a.application_id, "accept")}
                        disabled={decideBusyId === a.application_id}
                        data-testid={`league-accept-${a.application_id}`}
                        className="px-3 py-2 rounded-xl text-xs btn-neon disabled:opacity-50"
                      >ACCEPT</button>
                    </div>
                  ) : (
                    <span className={`text-[9px] font-bold tracking-widest px-2 py-1 rounded-full ${STATUS_STYLES[a.status] || ""}`}>
                      {a.status.toUpperCase()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )
        )}
        </div>
      </div>
      <BadgeShareModal
        badge={shareBadge}
        leagueName={data?.name}
        onClose={() => setShareBadge(null)}
        onToast={onToast}
      />
      <LeagueEditModal
        open={editOpen}
        league={data}
        onClose={() => setEditOpen(false)}
        onSaved={() => load()}
        onToast={onToast}
      />
      {moderationOpen && (
        <LeagueModerationPanel
          league={data}
          onClose={() => setModerationOpen(false)}
          onToast={onToast}
        />
      )}
      {editingTeam && (
        <TeamEditModal
          team={editingTeam}
          isProLeague={data.league_type === "PRO"}
          onClose={() => setEditingTeam(null)}
          onSaved={() => { setEditingTeam(null); load(); }}
          onToast={onToast}
        />
      )}
      {challengeToTeam && (
        <ChallengeModal
          fromTeam={challengeFromTeam}
          toTeam={challengeToTeam}
          myManagedTeams={teams.filter((t) => t.captain_user_id === user?.user_id && t.team_id !== challengeToTeam.team_id)}
          onPickFromTeam={(t) => setChallengeFromTeam(t)}
          onClose={() => { setChallengeFromTeam(null); setChallengeToTeam(null); }}
          onSent={() => { onToast?.("⚡ Challenge sent — waiting on their reply."); setChallengeFromTeam(null); setChallengeToTeam(null); load(); }}
          onToast={onToast}
        />
      )}
      {resultChallenge && (
        <MatchResultModal
          challenge={resultChallenge}
          onClose={() => setResultChallenge(null)}
          onSaved={() => { setResultChallenge(null); load(); onToast?.("🏁 Result recorded. Leaderboards updated."); }}
          onToast={onToast}
        />
      )}
      {leagueDmTarget && (
        <LeagueDmModal
          fromLeague={data}
          toLeague={leagueDmTarget}
          onClose={() => setLeagueDmTarget(null)}
          onToast={onToast}
        />
      )}
    </div>
  );
}

export default LeagueDetailModal;

// =========================================================================
// MatchResultModal — enter the final score of an accepted challenge.
// =========================================================================
function MatchResultModal({ challenge, onClose, onSaved, onToast }) {
  const [scoreA, setScoreA] = useState("");
  const [scoreB, setScoreB] = useState("");
  const [kdaA, setKdaA] = useState({ kills: "", deaths: "", assists: "" });
  const [kdaB, setKdaB] = useState({ kills: "", deaths: "", assists: "" });
  const [videoUrl, setVideoUrl] = useState("");
  const [clutch, setClutch] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasVideo = !!(videoUrl || "").trim();
  const submit = async (e) => {
    e.preventDefault();
    const a = Number(scoreA), b = Number(scoreB);
    if (Number.isNaN(a) || Number.isNaN(b)) { onToast?.("Enter both scores."); return; }
    if (a === b) { onToast?.("Score can't be a tie — one team has to win."); return; }
    const toInt = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.max(0, Math.min(9999, Math.floor(n))) : 0;
    };
    const cleanA = { kills: toInt(kdaA.kills), deaths: toInt(kdaA.deaths), assists: toInt(kdaA.assists) };
    const cleanB = { kills: toInt(kdaB.kills), deaths: toInt(kdaB.deaths), assists: toInt(kdaB.assists) };
    setBusy(true);
    try {
      await recordChallengeResult(challenge.challenge_id, {
        score_a: a,
        score_b: b,
        kda_a: cleanA,
        kda_b: cleanB,
        video_url: (videoUrl || "").trim() || undefined,
        clutch: hasVideo && clutch,
      });
      onSaved?.();
    } catch (ex) {
      onToast?.(ex?.response?.data?.detail || "Couldn't record result.");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="match-result-modal"
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form onSubmit={submit} className="glass-strong neon-border-[#14b8a6] rounded-3xl w-full max-w-md p-6 my-8">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-1">
          <i className="fas fa-flag-checkered mr-1" />ENTER MATCH RESULT
        </p>
        <p className="font-display text-2xl mb-4">
          {challenge.from_team?.name} <span className="grad-text">vs</span> {challenge.to_team?.name}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] tracking-widest text-white/65">
              {challenge.from_team?.name} SCORE
            </span>
            <input
              type="number"
              min={0} max={999}
              value={scoreA}
              onChange={(e) => setScoreA(e.target.value)}
              autoFocus
              required
              data-testid="match-result-score-a"
              className="mt-1 w-full bg-black/50 border border-[#14b8a6]/40 rounded-xl px-3 py-3 text-white text-center text-2xl font-display"
            />
          </label>
          <label className="block">
            <span className="text-[10px] tracking-widest text-white/65">
              {challenge.to_team?.name} SCORE
            </span>
            <input
              type="number"
              min={0} max={999}
              value={scoreB}
              onChange={(e) => setScoreB(e.target.value)}
              required
              data-testid="match-result-score-b"
              className="mt-1 w-full bg-black/50 border border-[#14b8a6]/40 rounded-xl px-3 py-3 text-white text-center text-2xl font-display"
            />
          </label>
        </div>

        {/* K/D/A — optional combat stats. Self-reported. */}
        <div className="mt-4 rounded-2xl border border-[#14b8a6]/25 bg-[#14b8a6]/5 p-3">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            <i className="fas fa-crosshairs mr-1" />COMBAT STATS (optional)
          </p>
          <p className="text-[10px] text-white/45 -mt-0.5">
            Kills · Deaths · Assists per team total. Splits evenly across the roster on the leaderboard.
          </p>
          <KdaRow label={challenge.from_team?.name} kda={kdaA} onChange={setKdaA} testidPrefix="match-result-kda-a" />
          <KdaRow label={challenge.to_team?.name}   kda={kdaB} onChange={setKdaB} testidPrefix="match-result-kda-b" />
        </div>

        {/* Video URL */}
        <label className="block mt-4">
          <span className="text-[10px] tracking-[3px] text-[#a3a3ac]">
            <i className="fas fa-circle-play mr-1" />HIGHLIGHT URL (optional)
          </span>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://youtu.be/... or streamable.com/... or clips.twitch.tv/..."
            data-testid="match-result-video-url"
            className="mt-1 w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-[12px] text-white"
          />
          <p className="text-[10px] text-white/40 mt-1">
            Accepts YouTube, Streamable, or Twitch clips. Renders inline on team / player profiles.
          </p>
        </label>

        <label
          className={`mt-3 flex items-center gap-2 rounded-2xl border p-3 cursor-pointer transition ${
            clutch && hasVideo
              ? "border-[#14b8a6]/60 bg-[#14b8a6]/10"
              : "border-white/10 bg-black/30 hover:border-white/30"
          } ${!hasVideo ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <input
            type="checkbox"
            checked={clutch && hasVideo}
            disabled={!hasVideo}
            onChange={(e) => setClutch(e.target.checked)}
            data-testid="match-result-clutch"
            className="accent-[#14b8a6]"
          />
          <span className="text-[12px] text-white">
            <i className="fas fa-fire text-[#14b8a6] mr-1" />
            <b>Mark as clutch</b>
            <span className="block text-[10px] text-white/45 mt-0.5">
              Featured on the Clutch Moments rail{!hasVideo && " (add a highlight URL to enable)"}.
            </span>
          </span>
        </label>

        <p className="text-[10px] text-white/45 mt-4">
          Winners get +25 leaderboard points each; losers get +5 to keep
          engagement rewarded. Stats are self-reported — disputes go through the inbox.
        </p>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose}
            data-testid="match-result-cancel"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost">
            CANCEL
          </button>
          <button type="submit" disabled={busy}
            data-testid="match-result-submit"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-60">
            {busy ? "…" : "RECORD RESULT"}
          </button>
        </div>
      </form>
    </div>
  );
}

function KdaRow({ label, kda, onChange, testidPrefix }) {
  const set = (k, v) => onChange({ ...kda, [k]: v });
  return (
    <div className="mt-2 flex items-center gap-2">
      <span className="text-[10px] text-white/65 truncate w-24">{label}</span>
      <input
        type="number" min={0} max={9999} placeholder="K"
        value={kda.kills}
        onChange={(e) => set("kills", e.target.value)}
        data-testid={`${testidPrefix}-kills`}
        className="flex-1 bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-center text-white text-sm"
      />
      <input
        type="number" min={0} max={9999} placeholder="D"
        value={kda.deaths}
        onChange={(e) => set("deaths", e.target.value)}
        data-testid={`${testidPrefix}-deaths`}
        className="flex-1 bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-center text-white text-sm"
      />
      <input
        type="number" min={0} max={9999} placeholder="A"
        value={kda.assists}
        onChange={(e) => set("assists", e.target.value)}
        data-testid={`${testidPrefix}-assists`}
        className="flex-1 bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-center text-white text-sm"
      />
    </div>
  );
}

// =========================================================================
// LeagueDmModal — owner-to-owner DM thread between two leagues.
// =========================================================================
function LeagueDmModal({ fromLeague, toLeague, onClose, onToast }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = React.useRef(null);
  const load = async () => {
    try { setMessages(await fetchLeagueDmThread(fromLeague.league_id, toLeague.league_id)); }
    catch (e) { onToast?.(e?.response?.data?.detail || "Couldn't load thread."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages.length]);
  const submit = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      const msg = await sendLeagueDm(fromLeague.league_id, toLeague.league_id, text);
      setMessages((p) => [...p, msg]);
      setBody("");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't send.");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="league-dm-modal"
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="glass-strong neon-border-[#14b8a6] rounded-3xl w-full max-w-lg p-5 flex flex-col" style={{ maxHeight: "82vh" }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-comments mr-1" />LEAGUE → LEAGUE DM
            </p>
            <p className="font-display text-xl">
              {fromLeague.name} <i className="fas fa-arrow-right text-xs text-white/50" /> {toLeague.name}
            </p>
          </div>
          <button onClick={onClose} data-testid="league-dm-close" className="w-9 h-9 rounded-xl btn-ghost">
            <i className="fas fa-xmark" />
          </button>
        </div>
        <div data-testid="league-dm-thread" className="flex-1 overflow-y-auto space-y-2 pr-1 mb-2">
          {messages.length === 0 ? (
            <p className="text-[12px] text-white/50 text-center mt-6">
              No messages yet — break the ice with a tournament pitch.
            </p>
          ) : messages.map((m) => {
            const mine = m.from_league_id === fromLeague.league_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-[12px] ${
                  mine ? "bg-[#14b8a6]/20 border border-[#14b8a6]/40" : "bg-black/40 border border-white/10"
                }`}>
                  <p className="text-[9px] font-bold tracking-wide text-[#a3a3ac]">
                    {mine ? fromLeague.name : toLeague.name}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <form onSubmit={submit} className="flex gap-2">
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            placeholder={`Message ${toLeague.name}…`}
            data-testid="league-dm-input"
            className="flex-1 bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
          />
          <button type="submit" disabled={busy || !body.trim()}
            data-testid="league-dm-send"
            className="text-[11px] tracking-widest px-4 rounded-xl btn-neon disabled:opacity-60">
            {busy ? "..." : "SEND"}
          </button>
        </form>
      </div>
    </div>
  );
}


/**
 * LeagueLadderTab — compact ladder view embedded inside LeagueDetailModal.
 * Reuses the public /api/leagues/by-slug/:slug/ladder endpoint so the same
 * data shape powers both the standalone page and the in-modal tab.
 */
function LeagueLadderTab({ slug, onToast }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchLeagueLadder(slug)
      .then((d) => { setData(d); setErr(""); })
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Couldn't load ladder.";
        setErr(msg);
        onToast?.(msg);
      })
      .finally(() => setLoading(false));
  }, [slug, onToast]);

  if (loading) {
    return (
      <p
        data-testid="league-modal-ladder-loading"
        className="text-[11px] tracking-widest text-[#14b8a6] animate-pulse"
      >
        LOADING LADDER…
      </p>
    );
  }
  if (err || !data) {
    return (
      <p data-testid="league-modal-ladder-error" className="text-[11px] text-[#14b8a6]">
        {err || "Ladder unavailable."}
      </p>
    );
  }
  const { rows, total_teams } = data;

  return (
    <div data-testid="league-modal-ladder">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-trophy mr-1" />MMR LADDER · {total_teams} TEAM{total_teams === 1 ? "" : "S"}
        </p>
        <a
          href={`/rankings/leagues/${slug}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="league-modal-ladder-share"
          className="text-[10px] tracking-widest text-[#14b8a6] hover:text-white"
        >
          <i className="fas fa-external-link-alt mr-1" />SHARE PAGE
        </a>
      </div>

      {rows.length === 0 ? (
        <p
          data-testid="league-modal-ladder-empty"
          className="text-[11px] text-white/45"
        >
          No teams have played yet — be the first to climb.
        </p>
      ) : (
        <div className="glass rounded-2xl overflow-hidden border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-widest text-white/45 border-b border-white/10">
                <th className="text-left px-3 py-2 w-12">#</th>
                <th className="text-left px-2 py-2">TEAM</th>
                <th className="text-left px-2 py-2 hidden md:table-cell">TIER</th>
                <th className="text-center px-2 py-2 w-12">W</th>
                <th className="text-center px-2 py-2 w-12">L</th>
                <th className="text-right px-3 py-2 w-16">MMR</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.team_id}
                  data-testid={`league-modal-ladder-row-${r.team_id}`}
                  className="border-b border-white/5 hover:bg-white/[0.03]"
                >
                  <td className="px-3 py-2 font-mono text-white/65">
                    {r.rank <= 3
                      ? <span className="text-[#14b8a6] font-bold">{["🥇","🥈","🥉"][r.rank - 1]}</span>
                      : r.rank}
                  </td>
                  <td className="px-2 py-2 flex items-center gap-2 min-w-0">
                    <AvatarChip avatarId={r.avatar_id} pfpUrl={r.pfp_url} size={28} />
                    <span className="truncate text-white">{r.name}</span>
                  </td>
                  <td className="px-2 py-2 hidden md:table-cell">
                    <TierBadge tier={r.tier} mmr={null} compact />
                  </td>
                  <td className="px-2 py-2 text-center text-[#14b8a6] font-mono">{r.wins}</td>
                  <td className="px-2 py-2 text-center text-white/55 font-mono">{r.losses}</td>
                  <td className="px-3 py-2 text-right font-mono text-white font-bold">{r.mmr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
